import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, ChevronDown, ChevronUp, Upload, Image as ImageIcon, Video, X, Link as LinkIcon, Check } from "lucide-react";
import api from "@/lib/api";

// ------- Primitives -------
const inputCls =
  "w-full bg-transparent border border-white/12 focus:border-[#FFBF57] outline-none px-3 py-2 text-sm text-[#F4F4F5] placeholder-neutral-500 rounded-md transition-colors";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function toFullUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/")) return `${BACKEND}${u}`;
  return u;
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#D4D4D8] mb-1.5 block font-semibold">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-[#8F8F88] mt-1.5">{hint}</div>}
    </div>
  );
}

function Section({ title, subtitle, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/10 bg-[#131317] rounded-2xl overflow-hidden">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/[0.03] transition-colors" data-testid={`cms-section-${title.replace(/\s+/g, "-").toLowerCase()}`}>
        <div className="text-left">
          <div className="font-display text-xl text-[#F4F4F5]">{title}</div>
          {subtitle && <div className="text-xs text-[#D4D4D8] mt-0.5">{subtitle}</div>}
        </div>
        {open ? <ChevronUp size={18} className="text-[#D4D4D8]" /> : <ChevronDown size={18} className="text-[#D4D4D8]" />}
      </button>
      {open && <div className="px-6 py-6 border-t border-white/10">{children}</div>}
    </div>
  );
}

function SaveBar({ onSave, saving, testId }) {
  return (
    <div className="flex justify-end mt-6">
      <button onClick={onSave} disabled={saving} className="btn-primary !py-2 !px-4 !text-xs disabled:opacity-50" data-testid={testId || "cms-save"}>
        <Save size={14} strokeWidth={1.5} /> {saving ? "Guardando…" : "Guardar cambios"}
      </button>
    </div>
  );
}

// ------- Upload dropzone (image or video) -------
function Dropzone({ value, onChange, accept = "image/*", kind = "image", compact = false }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showUrl, setShowUrl] = useState(false);
  const [justUploaded, setJustUploaded] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    setProgress(0);
    try {
      const r = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      onChange(r.data.url);
      setJustUploaded(true);
      setTimeout(() => setJustUploaded(false), 2500);
      toast.success("Archivo subido correctamente");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir el archivo");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const previewUrl = toFullUrl(value);
  const isVideo = kind === "video";

  return (
    <div className="space-y-2">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`relative border-2 border-dashed rounded-xl transition-all cursor-pointer ${
          dragging
            ? "border-[#FFBF57] bg-[#FFBF57]/8 scale-[1.01]"
            : justUploaded
              ? "border-emerald-500/60 bg-emerald-500/5"
              : "border-white/20 bg-white/[0.02] hover:border-[#FFBF57]/60 hover:bg-white/[0.04]"
        } ${compact ? "p-3" : "p-5"}`}
        onClick={() => inputRef.current?.click()}
        data-testid="cms-dropzone"
      >
        <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="cms-file-input" />
        {previewUrl ? (
          <div className="flex items-center gap-3">
            {isVideo ? (
              <video src={previewUrl} className="w-24 h-24 rounded-lg object-cover bg-black border border-white/10" />
            ) : (
              <img src={previewUrl} alt="preview" className="w-24 h-24 rounded-lg object-cover bg-black border border-white/10" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-emerald-300 font-mono">
                {justUploaded && <Check size={12} />} {isVideo ? "Video adjunto" : "Imagen adjunta"}
              </div>
              <div className="text-[11px] text-[#8F8F88] truncate mt-1">Servido desde tu panel · no depende de links externos</div>
              {uploading && (
                <div className="mt-2 h-1 bg-white/10 rounded overflow-hidden">
                  <div className="h-full bg-[#FFBF57] transition-all" style={{ width: `${progress}%` }} />
                </div>
              )}
              <div className="flex items-center gap-2 mt-2">
                <button type="button" onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }} className="text-[10px] uppercase tracking-widest font-mono text-[#D4D4D8] hover:text-[#FFBF57] transition-colors">
                  Reemplazar
                </button>
                <span className="text-[#3A3A42]">·</span>
                <button type="button" onClick={(e) => { e.stopPropagation(); onChange(""); }} className="text-[10px] uppercase tracking-widest font-mono text-red-400/80 hover:text-red-400 transition-colors">
                  Quitar
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#FFBF57]/15 to-[#FFBF57]/5 border border-[#FFBF57]/30 flex items-center justify-center">
              {isVideo ? <Video size={20} className="text-[#FFBF57]" /> : <ImageIcon size={20} className="text-[#FFBF57]" />}
            </div>
            <div className="flex-1">
              <div className="text-sm text-[#F4F4F5] font-medium flex items-center gap-2">
                {uploading ? (
                  <>Subiendo… <span className="num-mono text-[#FFBF57]">{progress}%</span></>
                ) : (
                  <>
                    <span>Arrastra un archivo o haz clic para subir</span>
                    <Upload size={13} className="text-[#FFBF57]" />
                  </>
                )}
              </div>
              <div className="text-[11px] text-[#8F8F88] mt-1">
                {isVideo ? "MP4 vertical hasta 100MB · Se guarda en tu panel" : "JPG · PNG · WebP hasta 100MB · Se guarda en tu panel"}
              </div>
              {uploading && (
                <div className="mt-2 h-1 bg-white/10 rounded overflow-hidden">
                  <div className="h-full bg-[#FFBF57] transition-all" style={{ width: `${progress}%` }} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Optional URL fallback — collapsed by default */}
      {!showUrl ? (
        <button
          type="button"
          onClick={() => setShowUrl(true)}
          className="text-[10px] uppercase tracking-widest font-mono text-[#8F8F88] hover:text-[#D4D4D8] transition-colors inline-flex items-center gap-1.5"
        >
          <LinkIcon size={10} /> Usar URL externa (opcional)
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <input
            className={inputCls + " !text-[11px] !py-1.5"}
            placeholder="Pega una URL externa (opcional)"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          <button type="button" onClick={() => setShowUrl(false)} className="text-[#8F8F88] hover:text-[#FFBF57] p-1" title="Ocultar">
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

// ------- Nested sucursales editor -------
const SUCURSAL_STATES = [
  { v: "confirmado", l: "Confirmada" },
  { v: "en_operacion", l: "Ya abrió (en operación)" },
  { v: "en_remodelacion", l: "Próxima apertura · en remodelación" },
  { v: "en_negociacion", l: "En negociación" },
  { v: "en_documentacion", l: "En documentación" },
  { v: "prospectada", l: "Prospectada" },
];

function NestedSucursales({ items, onChange }) {
  const list = Array.isArray(items) ? items : [];
  const set = (i, patch) => {
    const next = list.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const add = () => onChange([...list, { nombre: "", ciudad: "", estado: "prospectada", descripcion: "", image_url: "" }]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = list.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {list.map((s, i) => (
        <div key={i} className="border border-white/10 bg-[#0A0A0B] rounded-lg p-3 space-y-2" data-testid={`cms-sucursal-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest text-[#8F8F88]">Sucursal {i + 1}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="text-neutral-500 hover:text-[#FFBF57] disabled:opacity-30 text-xs px-1.5">↑</button>
              <button onClick={() => move(i, 1)} disabled={i === list.length - 1} className="text-neutral-500 hover:text-[#FFBF57] disabled:opacity-30 text-xs px-1.5">↓</button>
              <button onClick={() => remove(i)} className="text-red-400/70 hover:text-red-400 p-1"><Trash2 size={12} /></button>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-2">
            <input className={inputCls + " !text-[12px]"} placeholder="Nombre (Sucursal Polanco)" value={s.nombre || ""} onChange={(e) => set(i, { nombre: e.target.value })} />
            <input className={inputCls + " !text-[12px]"} placeholder="Ciudad" value={s.ciudad || ""} onChange={(e) => set(i, { ciudad: e.target.value })} />
            <select className={inputCls + " !text-[12px]"} value={s.estado || "prospectada"} onChange={(e) => set(i, { estado: e.target.value })}>
              {SUCURSAL_STATES.map((st) => <option key={st.v} value={st.v}>{st.l}</option>)}
            </select>
          </div>
          <textarea className={inputCls + " !text-[12px] min-h-[50px]"} placeholder="Descripción corta" value={s.descripcion || ""} onChange={(e) => set(i, { descripcion: e.target.value })} />
          <input className={inputCls + " !text-[12px]"} placeholder="Tipo / plaza (ej. Soriana Híper · nuevo desarrollo comercial)" value={s.tipo || ""} onChange={(e) => set(i, { tipo: e.target.value })} />
          <textarea className={inputCls + " !text-[12px] min-h-[70px]"} placeholder="Datos importantes (uno por línea) · se muestran como lista con palomitas" value={(s.highlights || []).join("\n")} onChange={(e) => set(i, { highlights: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} />
          <textarea className={inputCls + " !text-[12px] min-h-[110px]"} placeholder="Texto completo para la página del proyecto (párrafos separados por línea en blanco)" value={s.descripcion_larga || ""} onChange={(e) => set(i, { descripcion_larga: e.target.value })} />
          <Dropzone value={s.image_url || ""} onChange={(v) => set(i, { image_url: v })} compact />
        </div>
      ))}
      <button onClick={add} className="w-full border border-dashed border-white/15 hover:border-[#FFBF57]/50 rounded-lg py-2 text-[11px] uppercase tracking-widest text-[#D4D4D8] hover:text-[#FFBF57] flex items-center justify-center gap-2">
        <Plus size={12} /> Agregar sucursal
      </button>
    </div>
  );
}

// ------- Reusable list editor -------
function ItemsEditor({ items, onChange, template, fields, testIdPrefix, compact = false }) {
  const setItem = (i, patch) => {
    const next = items.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const add = () => onChange([...(items || []), { ...template }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className={`border border-white/10 bg-[#1B1B21] rounded-xl ${compact ? "p-3" : "p-4"} space-y-3`} data-testid={`${testIdPrefix}-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest text-[#8F8F88] font-semibold">Ítem {i + 1}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="text-neutral-500 hover:text-[#FFBF57] disabled:opacity-30 text-xs px-1.5">↑</button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-neutral-500 hover:text-[#FFBF57] disabled:opacity-30 text-xs px-1.5">↓</button>
              <button onClick={() => remove(i)} className="text-red-400/70 hover:text-red-400 p-1" data-testid={`${testIdPrefix}-${i}-remove`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
          {fields.map((f) => (
            <Field key={f.key} label={f.label} hint={f.hint}>
              {f.type === "textarea" ? (
                <textarea className={inputCls + " min-h-[70px]"} value={it[f.key] || ""} onChange={(e) => setItem(i, { [f.key]: e.target.value })} />
              ) : f.type === "image" || f.type === "video" ? (
                <Dropzone
                  value={it[f.key] || ""}
                  onChange={(v) => setItem(i, { [f.key]: v })}
                  accept={f.type === "video" ? "video/*" : "image/*"}
                  kind={f.type}
                />
              ) : f.type === "select" ? (
                <select className={inputCls} value={it[f.key] || ""} onChange={(e) => setItem(i, { [f.key]: e.target.value })}>
                  {(f.options || []).map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              ) : f.type === "nested-sucursales" ? (
                <NestedSucursales items={it[f.key] || []} onChange={(v) => setItem(i, { [f.key]: v })} />
              ) : (
                <input className={inputCls} value={it[f.key] || ""} onChange={(e) => setItem(i, { [f.key]: e.target.value })} />
              )}
            </Field>
          ))}
        </div>
      ))}
      <button onClick={add} className="w-full border border-dashed border-white/15 hover:border-[#FFBF57]/50 rounded-xl py-3 text-xs uppercase tracking-widest text-[#D4D4D8] hover:text-[#FFBF57] transition-colors flex items-center justify-center gap-2" data-testid={`${testIdPrefix}-add`}>
        <Plus size={14} /> Agregar ítem
      </button>
    </div>
  );
}

// ------- Main CMS Panel -------
export default function CmsPanel() {
  const [blocks, setBlocks] = useState({});
  const [saving, setSaving] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/content-blocks");
      const map = {};
      for (const b of r.data) map[b.key] = b.content || {};
      setBlocks(map);
    } catch (_e) {
      toast.error("No se pudo cargar el contenido");
    }
  };

  useEffect(() => { load(); }, []);

  const update = (key, patch) => {
    setBlocks((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
  };

  const save = async (key) => {
    setSaving(key);
    try {
      const payload = blocks[key] || {};
      await api.patch(`/content-blocks/${key}`, { content: payload });
      toast.success(`Bloque "${key}" guardado`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="border border-[#FFBF57]/30 bg-gradient-to-br from-[#FFBF57]/[0.06] to-transparent rounded-2xl p-5 flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-[#FFBF57]/20 border border-[#FFBF57]/40 flex items-center justify-center shrink-0">
          <Upload size={16} className="text-[#FFBF57]" />
        </div>
        <div>
          <div className="text-sm text-[#F4F4F5] font-medium">Todas las fotos y videos se suben directamente aquí</div>
          <div className="text-xs text-[#D4D4D8] mt-1 leading-relaxed">Arrastra archivos o haz clic en cualquier zona con borde punteado. No necesitas Google Drive, Dropbox o URLs externas — los archivos se guardan en tu panel y se sirven al landing automáticamente.</div>
        </div>
      </div>

      {/* Layout · mostrar / ocultar secciones */}
      <Section title="Layout · Mostrar u ocultar secciones" subtitle="Marca la casilla para ocultar la sección en el landing público" defaultOpen>
        <div className="grid md:grid-cols-2 gap-2">
          {(blocks.layout?.sections || []).map((s) => {
            const hidden = (blocks.layout?.hidden || []).includes(s.key);
            const toggle = () => {
              const cur = new Set(blocks.layout?.hidden || []);
              if (hidden) cur.delete(s.key); else cur.add(s.key);
              update("layout", { hidden: Array.from(cur) });
            };
            return (
              <label key={s.key} className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all ${hidden ? "border-red-500/30 bg-red-500/[0.04]" : "border-white/10 bg-white/[0.02] hover:border-[#FFBF57]/40"}`} data-testid={`cms-toggle-${s.key}`}>
                <div>
                  <div className="text-sm text-[#F4F4F5]">{s.label}</div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-[#8F8F88] mt-0.5">{s.key}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`font-mono text-[10px] uppercase tracking-widest ${hidden ? "text-red-300" : "text-emerald-300"}`}>{hidden ? "Oculta" : "Visible"}</span>
                  <input type="checkbox" checked={!hidden} onChange={toggle} className="w-4 h-4 accent-[#FFBF57] cursor-pointer" />
                </div>
              </label>
            );
          })}
        </div>
        <SaveBar onSave={() => save("layout")} saving={saving === "layout"} testId="cms-save-layout" />
      </Section>

      {/* Simulador de ganancias */}
      <Section title="Simulador de ganancias" subtitle="Sliders Capital + Horizonte con proyección visual y disclaimer AEP" defaultOpen>
        <div className="grid gap-4">
          <Field label="Eyebrow"><input className={inputCls} value={blocks.simulator?.eyebrow || ""} onChange={(e) => update("simulator", { eyebrow: e.target.value })} /></Field>
          <Field label="Título" hint="Usa <em>palabra</em> para acento moderno"><input className={inputCls} value={blocks.simulator?.title || ""} onChange={(e) => update("simulator", { title: e.target.value })} /></Field>
          <Field label="Subtítulo"><textarea className={inputCls + " min-h-[70px]"} value={blocks.simulator?.subtitle || ""} onChange={(e) => update("simulator", { subtitle: e.target.value })} /></Field>
          <Field label="Presets rápidos (montos MXN separados por coma)" hint="Ej. 250000, 500000, 1000000 — se muestran como chips clickeables">
            <input
              className={inputCls}
              value={(blocks.simulator?.presets || [250000, 500000, 1000000]).join(", ")}
              onChange={(e) => update("simulator", { presets: e.target.value.split(",").map((v) => Number(v.trim())).filter(Boolean) })}
              data-testid="cms-sim-presets"
            />
          </Field>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Etiqueta escenario conservador"><input className={inputCls} value={blocks.simulator?.label_conservador || ""} onChange={(e) => update("simulator", { label_conservador: e.target.value })} placeholder="Escenario conservador" data-testid="cms-sim-label-conservador" /></Field>
            <Field label="Etiqueta escenario objetivo"><input className={inputCls} value={blocks.simulator?.label_objetivo || ""} onChange={(e) => update("simulator", { label_objetivo: e.target.value })} placeholder="Escenario objetivo" data-testid="cms-sim-label-objetivo" /></Field>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="Capital mínimo (MXN)"><input type="number" className={inputCls} value={blocks.simulator?.min_capital ?? 250000} onChange={(e) => update("simulator", { min_capital: Number(e.target.value) })} /></Field>
            <Field label="Capital máximo (MXN)"><input type="number" className={inputCls} value={blocks.simulator?.max_capital ?? 5000000} onChange={(e) => update("simulator", { max_capital: Number(e.target.value) })} /></Field>
            <Field label="Step (MXN)"><input type="number" className={inputCls} value={blocks.simulator?.step_capital ?? 50000} onChange={(e) => update("simulator", { step_capital: Number(e.target.value) })} /></Field>
            <Field label="Capital default"><input type="number" className={inputCls} value={blocks.simulator?.default_capital ?? 500000} onChange={(e) => update("simulator", { default_capital: Number(e.target.value) })} /></Field>
            <Field label="Horizonte mínimo (años)"><input type="number" className={inputCls} value={blocks.simulator?.min_horizon ?? 2} onChange={(e) => update("simulator", { min_horizon: Number(e.target.value) })} /></Field>
            <Field label="Horizonte máximo (años)"><input type="number" className={inputCls} value={blocks.simulator?.max_horizon ?? 8} onChange={(e) => update("simulator", { max_horizon: Number(e.target.value) })} /></Field>
            <Field label="Horizonte default"><input type="number" className={inputCls} value={blocks.simulator?.default_horizon ?? 5} onChange={(e) => update("simulator", { default_horizon: Number(e.target.value) })} /></Field>
            <Field label="Vigencia de la participación (años)" hint="Se muestra como '{{years}} años' en el simulador"><input type="number" className={inputCls} value={blocks.simulator?.horizon_years ?? 5} onChange={(e) => update("simulator", { horizon_years: Number(e.target.value) })} data-testid="cms-sim-years" /></Field>
            <Field label="Capital total del proyecto (para % de participación)"><input type="number" className={inputCls} value={blocks.simulator?.project_capital ?? 5000000} onChange={(e) => update("simulator", { project_capital: Number(e.target.value) })} data-testid="cms-sim-project-capital" /></Field>
            <Field label="Tasa conservador (0-1)" hint="0.12 = 12% anual"><input type="number" step="0.01" className={inputCls} value={blocks.simulator?.rate_conservador ?? 0.12} onChange={(e) => update("simulator", { rate_conservador: Number(e.target.value) })} /></Field>
            <Field label="Tasa objetivo (0-1)" hint="0.22 = 22% anual"><input type="number" step="0.01" className={inputCls} value={blocks.simulator?.rate_objetivo ?? 0.22} onChange={(e) => update("simulator", { rate_objetivo: Number(e.target.value) })} /></Field>
          </div>
          <Field label="Disclaimer (aparece al pie del simulador)" hint="Institucional · sin promesas de rendimiento — recomendado dejarlo tal cual salvo revisión legal">
            <textarea className={inputCls + " min-h-[120px]"} value={blocks.simulator?.disclaimer || ""} onChange={(e) => update("simulator", { disclaimer: e.target.value })} data-testid="cms-sim-disclaimer" />
          </Field>
          <Field label="Pasos 'Así funciona tu participación' (3)" hint="Usa {{min}} para el capital mínimo y {{years}} para la vigencia">
            <ItemsEditor
              items={blocks.simulator?.terms?.length ? blocks.simulator.terms : [
                { title: "Participas desde {{min}}", body: "Eliges el monto con el que entras al proyecto." },
                { title: "{{years}} años recibiendo utilidades", body: "La participación tiene una vigencia de {{years}} años con distribución mensual de utilidades reales." },
                { title: "Recompra + siguiente proyecto", body: "Al término, Rubí recompra tu participación y puedes volver a participar en otro proyecto de expansión." },
              ]}
              onChange={(terms) => update("simulator", { terms })}
              testIdPrefix="cms-sim-term"
              template={{ title: "", body: "" }}
              fields={[{ key: "title", label: "Título" }, { key: "body", label: "Descripción", type: "textarea" }]}
            />
          </Field>
        </div>
        <SaveBar onSave={() => save("simulator")} saving={saving === "simulator"} testId="cms-save-simulator" />
      </Section>

      {/* Investor Journey */}
      <Section title="Tu experiencia como inversionista (Journey)" subtitle="Recorrido de 7 pasos · Editable" defaultOpen>
        <div className="grid gap-4">
          <Field label="Eyebrow"><input className={inputCls} value={blocks.investor_journey?.eyebrow || ""} onChange={(e) => update("investor_journey", { eyebrow: e.target.value })} data-testid="cms-journey-eyebrow" /></Field>
          <Field label="Título" hint="Usa <em>palabra</em> para acento"><input className={inputCls} value={blocks.investor_journey?.title || ""} onChange={(e) => update("investor_journey", { title: e.target.value })} /></Field>
          <Field label="Subtítulo"><textarea className={inputCls + " min-h-[70px]"} value={blocks.investor_journey?.subtitle || ""} onChange={(e) => update("investor_journey", { subtitle: e.target.value })} /></Field>
          <Field label="Pasos" hint="Ordena, agrega o elimina pasos del recorrido">
            <ItemsEditor
              items={blocks.investor_journey?.steps || []}
              onChange={(steps) => update("investor_journey", { steps })}
              testIdPrefix="cms-journey"
              template={{ n: "", label: "", title: "", body: "" }}
              fields={[
                { key: "n", label: "Número (ej: 01)" },
                { key: "label", label: "Etiqueta corta (ej: Solicitud)" },
                { key: "title", label: "Título del paso" },
                { key: "body", label: "Descripción", type: "textarea" },
              ]}
            />
          </Field>
        </div>
        <SaveBar onSave={() => save("investor_journey")} saving={saving === "investor_journey"} testId="cms-save-journey" />
      </Section>

      {/* Capital Allocation */}
      <Section title="¿A dónde va mi dinero? (Distribución del capital)" subtitle="Donut interactivo · Editable con porcentajes y colores" defaultOpen>
        <div className="grid gap-4">
          <Field label="Eyebrow"><input className={inputCls} value={blocks.capital_allocation?.eyebrow || ""} onChange={(e) => update("capital_allocation", { eyebrow: e.target.value })} data-testid="cms-alloc-eyebrow" /></Field>
          <Field label="Título" hint="Usa <em>palabra</em> para acento"><input className={inputCls} value={blocks.capital_allocation?.title || ""} onChange={(e) => update("capital_allocation", { title: e.target.value })} /></Field>
          <Field label="Subtítulo"><textarea className={inputCls + " min-h-[70px]"} value={blocks.capital_allocation?.subtitle || ""} onChange={(e) => update("capital_allocation", { subtitle: e.target.value })} /></Field>
          <Field label="Partidas del capital" hint="Deben sumar 100% en total">
            <ItemsEditor
              items={blocks.capital_allocation?.items || []}
              onChange={(items) => update("capital_allocation", { items })}
              testIdPrefix="cms-alloc"
              template={{ label: "", pct: 10, color: "#FFBF57", note: "" }}
              fields={[
                { key: "label", label: "Etiqueta" },
                { key: "pct", label: "Porcentaje (0-100)" },
                { key: "color", label: "Color hex (ej: #FFBF57)" },
                { key: "note", label: "Nota corta", type: "textarea" },
              ]}
            />
          </Field>
          <Field label="Disclaimer" hint="Aparece al pie del donut">
            <textarea className={inputCls + " min-h-[90px]"} value={blocks.capital_allocation?.disclaimer || ""} onChange={(e) => update("capital_allocation", { disclaimer: e.target.value })} data-testid="cms-alloc-disclaimer" />
          </Field>
        </div>
        <SaveBar onSave={() => save("capital_allocation")} saving={saving === "capital_allocation"} testId="cms-save-alloc" />
      </Section>

      {/* Why Rubí */}
      <Section title="Por qué Rubí · pilares" subtitle="Bento con foto real de sucursal, frase gancho y 4 pilares (el primero se muestra en negro)">
        <div className="grid gap-4">
          <Field label="Eyebrow"><input className={inputCls} value={blocks.why_rubi?.eyebrow || ""} onChange={(e) => update("why_rubi", { eyebrow: e.target.value })} placeholder="Por qué Rubí" /></Field>
          <Field label="Título" hint="Usa <em>palabra</em> para acento"><input className={inputCls} value={blocks.why_rubi?.title || ""} onChange={(e) => update("why_rubi", { title: e.target.value })} data-testid="cms-why-title" /></Field>
          <Field label="Subtítulo"><textarea className={inputCls + " min-h-[70px]"} value={blocks.why_rubi?.subtitle || ""} onChange={(e) => update("why_rubi", { subtitle: e.target.value })} /></Field>
          <Field label="Frase gancho sobre la foto"><input className={inputCls} value={blocks.why_rubi?.quote || ""} onChange={(e) => update("why_rubi", { quote: e.target.value })} placeholder="No inviertes en una plataforma. Inviertes en una expansión que puedes ver." data-testid="cms-why-quote" /></Field>
          <Field label="Foto (opcional)" hint="Si se deja vacío se usa la foto de una sucursal del mapa">
            <Dropzone value={blocks.why_rubi?.image_url || ""} onChange={(v) => update("why_rubi", { image_url: v || "" })} accept="image/*" kind="image" />
          </Field>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Pie de foto"><input className={inputCls} value={blocks.why_rubi?.image_caption || ""} onChange={(e) => update("why_rubi", { image_caption: e.target.value })} /></Field>
            <Field label="Dato 1 (auto: N sucursales)"><input className={inputCls} value={blocks.why_rubi?.stat_1 || ""} onChange={(e) => update("why_rubi", { stat_1: e.target.value })} /></Field>
            <Field label="Dato 2 (auto: N estados)"><input className={inputCls} value={blocks.why_rubi?.stat_2 || ""} onChange={(e) => update("why_rubi", { stat_2: e.target.value })} /></Field>
          </div>
          <Field label="Mensaje al pie (legal / contexto)"><textarea className={inputCls + " min-h-[70px]"} value={blocks.why_rubi?.message || ""} onChange={(e) => update("why_rubi", { message: e.target.value })} /></Field>
          <Field label="Pilares">
            <ItemsEditor
              items={blocks.why_rubi?.cards || []}
              onChange={(cards) => update("why_rubi", { cards })}
              testIdPrefix="cms-why"
              template={{ title: "", body: "", metric: "" }}
              fields={[
                { key: "title", label: "Título" },
                { key: "body", label: "Descripción", type: "textarea" },
                { key: "metric", label: "Dato destacado (chip, opcional)" },
              ]}
            />
          </Field>
        </div>
        <SaveBar onSave={() => save("why_rubi")} saving={saving === "why_rubi"} testId="cms-save-why" />
      </Section>

      {/* Comparison */}
      <Section title="Comparador · esfuerzo, estructura y rendimiento" subtitle="CETES · Negocio propio · Proyecto Diamante (resaltado)" defaultOpen>
        <div className="grid gap-4">
          <Field label="Eyebrow"><input className={inputCls} value={blocks.comparison?.eyebrow || ""} onChange={(e) => update("comparison", { eyebrow: e.target.value })} placeholder="Comparador · Esfuerzo, estructura y rendimiento" /></Field>
          <Field label="Gancho principal (H2)" hint="Usa <em>palabra</em> para acento"><input className={inputCls} value={blocks.comparison?.hook || ""} onChange={(e) => update("comparison", { hook: e.target.value })} placeholder="Tu capital puede participar en un negocio sin obligarte a construirlo desde cero." data-testid="cms-comparison-hook" /></Field>
          <Field label="Sub-gancho"><textarea className={inputCls + " min-h-[60px]"} value={blocks.comparison?.subhook || ""} onChange={(e) => update("comparison", { subhook: e.target.value })} /></Field>
          <Field label="Título legado (ya no se muestra)"><input className={inputCls} value={blocks.comparison?.title || ""} onChange={(e) => update("comparison", { title: e.target.value })} data-testid="cms-comparison-title" /></Field>
          <Field label="Frase de cierre (banner negro)" hint="Usa <em>palabra</em> para acento"><input className={inputCls} value={blocks.comparison?.closing || ""} onChange={(e) => update("comparison", { closing: e.target.value })} placeholder="No inviertes en una plataforma. Inviertes en una expansión que puedes ver." /></Field>
          <Field label="Foto cabecera de Proyecto Diamante (opcional)" hint="Si se deja vacío se usa una foto de sucursal del mapa">
            <Dropzone value={blocks.comparison?.image_url || ""} onChange={(v) => update("comparison", { image_url: v || "" })} accept="image/*" kind="image" />
          </Field>
          <Field label="Disclaimer"><textarea className={inputCls + " min-h-[60px]"} value={blocks.comparison?.disclaimer || ""} onChange={(e) => update("comparison", { disclaimer: e.target.value })} /></Field>
          <Field label="Columnas (3 opciones)" hint="Marca 'Resaltar' en Proyecto Diamante. Puntajes 1-5.">
            <ItemsEditor
              items={blocks.comparison?.columns || []}
              onChange={(columns) => update("comparison", { columns })}
              testIdPrefix="cms-comp"
              template={{ name: "", subtitle: "", badge: "", rendimiento: "", rendimiento_score: 3, esfuerzo_score: 3, tiempo: "", involucramiento: "", estructura: "", upside: "", propio: "neutral", operas: "no", estructura_ok: "neutral", highlighted: "" }}
              fields={[
                { key: "name", label: "Nombre de la opción" },
                { key: "subtitle", label: "Subtítulo corto" },
                { key: "highlighted", label: "Resaltar (Proyecto Diamante)", type: "select", options: [{ v: "", l: "No" }, { v: "true", l: "Sí" }] },
                { key: "badge", label: "Badge (solo resaltada)" },
                { key: "rendimiento", label: "Rendimiento", type: "textarea" },
                { key: "rendimiento_score", label: "Puntaje rendimiento (1-5)" },
                { key: "esfuerzo_score", label: "Puntaje esfuerzo (1-5, menor es mejor)" },
                { key: "tiempo", label: "Tiempo requerido" },
                { key: "involucramiento", label: "Involucramiento", type: "textarea" },
                { key: "estructura", label: "Estructura que te respalda", type: "textarea" },
                { key: "upside", label: "Upside", type: "textarea" },
                { key: "propio", label: "¿Participas en un negocio real?", type: "select", options: [{ v: "yes", l: "Sí" }, { v: "no", l: "No" }, { v: "neutral", l: "Neutral" }] },
                { key: "operas", label: "¿Tienes que operarlo tú?", type: "select", options: [{ v: "yes", l: "Sí" }, { v: "no", l: "No" }] },
                { key: "estructura_ok", label: "¿Estructura ya construida?", type: "select", options: [{ v: "yes", l: "Sí" }, { v: "no", l: "No" }, { v: "neutral", l: "Neutral" }] },
              ]}
            />
          </Field>
        </div>
        <SaveBar onSave={() => save("comparison")} saving={saving === "comparison"} testId="cms-save-comparison" />
      </Section>

      {/* How it works */}
      <Section title="Cómo funciona (4 pasos) · Nuevo" subtitle="Recorrido corto de cómo aplicar" defaultOpen>
        <div className="grid gap-4">
          <Field label="Título" hint="Usa <em>palabra</em> para acento"><input className={inputCls} value={blocks.how_it_works?.title || ""} onChange={(e) => update("how_it_works", { title: e.target.value })} data-testid="cms-hiw-title" /></Field>
          <Field label="Pasos">
            <ItemsEditor
              items={blocks.how_it_works?.steps || []}
              onChange={(steps) => update("how_it_works", { steps })}
              testIdPrefix="cms-hiw"
              template={{ n: "", title: "", body: "" }}
              fields={[
                { key: "n", label: "Número (ej. 01)" },
                { key: "title", label: "Título del paso" },
                { key: "body", label: "Descripción", type: "textarea" },
              ]}
            />
          </Field>
        </div>
        <SaveBar onSave={() => save("how_it_works")} saving={saving === "how_it_works"} testId="cms-save-hiw" />
      </Section>

      {/* Trust Signals Marquee */}
      <Section title="Banda de confianza (Trust Signals)" subtitle="Marquee continuo con badges legales/financieros">
        <ItemsEditor
          items={blocks.trust_signals?.items || []}
          onChange={(items) => update("trust_signals", { items })}
          testIdPrefix="cms-trust"
          template={{ icon: "shield", label: "" }}
          fields={[
            { key: "icon", label: "Icono", type: "select", options: [
              { v: "shield", l: "Escudo" },
              { v: "lock", l: "Candado" },
              { v: "document", l: "Documento" },
              { v: "building", l: "Edificio" },
              { v: "award", l: "Trofeo" },
              { v: "trending", l: "Tendencia" },
              { v: "badge", l: "Distintivo" },
              { v: "sparkles", l: "Destello" },
            ]},
            { key: "label", label: "Texto (ej. 'AEP formal · 8 años operando')", type: "text" },
          ]}
        />
        <SaveBar onSave={() => save("trust_signals")} saving={saving === "trust_signals"} testId="cms-save-trust" />
      </Section>

      {/* Producto insignia */}
      <Section title="Producto insignia · Perfumes 3D" subtitle="3 frascos con parallax al mover el mouse">
        <div className="grid gap-4">
          <Field label="Eyebrow"><input className={inputCls} value={blocks.producto_insignia?.eyebrow || ""} onChange={(e) => update("producto_insignia", { eyebrow: e.target.value })} /></Field>
          <Field label="Título" hint="Usa <em>palabra</em> para acento italic"><input className={inputCls} value={blocks.producto_insignia?.title || ""} onChange={(e) => update("producto_insignia", { title: e.target.value })} /></Field>
          <Field label="Subtítulo"><textarea className={inputCls + " min-h-[70px]"} value={blocks.producto_insignia?.subtitle || ""} onChange={(e) => update("producto_insignia", { subtitle: e.target.value })} /></Field>
          <Field label="Microcopy inferior"><input className={inputCls} value={blocks.producto_insignia?.microcopy || ""} onChange={(e) => update("producto_insignia", { microcopy: e.target.value })} /></Field>
        </div>
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest text-[#D4D4D8] mb-2 font-semibold">Frascos (recomendado 3)</div>
          <ItemsEditor
            items={blocks.producto_insignia?.items || []}
            onChange={(items) => update("producto_insignia", { items })}
            testIdPrefix="cms-perfume"
            template={{ src: "", name: "", volume: "", tag: "" }}
            fields={[
              { key: "src", label: "Imagen del frasco (PNG con fondo transparente)", type: "image" },
              { key: "name", label: "Nombre (ej. Deluxe Edition · Signature)", type: "text" },
              { key: "volume", label: "Volumen (ej. 100 ml)", type: "text" },
              { key: "tag", label: "Etiqueta (ej. Icónico, Best seller)", type: "text" },
            ]}
          />
        </div>
        <SaveBar onSave={() => save("producto_insignia")} saving={saving === "producto_insignia"} testId="cms-save-producto" />
      </Section>

      {/* Brand */}
      <Section title="Marca y contacto" subtitle="Nombre visible + WhatsApp que se muestran en todo el sitio" defaultOpen>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Título de marca (superior)">
            <input className={inputCls} data-testid="cms-brand-title" value={blocks.brand?.title || ""} onChange={(e) => update("brand", { title: e.target.value })} placeholder="Expansión Rubí" />
          </Field>
          <Field label="Subtítulo">
            <input className={inputCls} data-testid="cms-brand-subtitle" value={blocks.brand?.subtitle || ""} onChange={(e) => update("brand", { subtitle: e.target.value })} placeholder="Proyecto Diamante" />
          </Field>
          <Field label="WhatsApp (dígitos, con lada país)" hint="Ej. 525550556647 (sin +)">
            <input className={inputCls} data-testid="cms-brand-whatsapp" value={blocks.brand?.whatsapp || ""} onChange={(e) => update("brand", { whatsapp: e.target.value.replace(/\D/g, "") })} />
          </Field>
          <Field label="WhatsApp (visible al usuario)">
            <input className={inputCls} data-testid="cms-brand-whatsapp-display" value={blocks.brand?.whatsapp_display || ""} onChange={(e) => update("brand", { whatsapp_display: e.target.value })} placeholder="+52 55 5055 6647" />
          </Field>
        </div>
        <SaveBar onSave={() => save("brand")} saving={saving === "brand"} testId="cms-save-brand" />
      </Section>

      {/* Hero */}
      <Section title="Hero — encabezado principal" subtitle="Lo primero que ve el visitante">
        <div className="grid gap-4">
          <Field label="Etiqueta superior (eyebrow)">
            <input className={inputCls} value={blocks.hero?.eyebrow || ""} onChange={(e) => update("hero", { eyebrow: e.target.value })} data-testid="cms-hero-eyebrow" />
          </Field>
          <Field label="Título principal (H1)" hint="Usa <em>palabra</em> para resaltar con acento (font Space Grotesk + subrayado amarillo).">
            <textarea className={inputCls + " min-h-[100px]"} value={blocks.hero?.title || ""} onChange={(e) => update("hero", { title: e.target.value })} data-testid="cms-hero-title" />
          </Field>
          <Field label="Subtítulo / párrafo">
            <textarea className={inputCls + " min-h-[90px]"} value={blocks.hero?.subtitle || ""} onChange={(e) => update("hero", { subtitle: e.target.value })} data-testid="cms-hero-subtitle" />
          </Field>
          <Field label="Microcopy inferior">
            <input className={inputCls} value={blocks.hero?.microcopy || ""} onChange={(e) => update("hero", { microcopy: e.target.value })} />
          </Field>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="CTA principal · texto">
              <input className={inputCls} value={blocks.hero?.cta_label || ""} onChange={(e) => update("hero", { cta_label: e.target.value })} placeholder="Conocer Proyecto Diamante" data-testid="cms-hero-cta-label" />
            </Field>
            <Field label="CTA principal · destino" hint="#proyectos, /calificacion o URL">
              <input className={inputCls} value={blocks.hero?.cta_href || ""} onChange={(e) => update("hero", { cta_href: e.target.value })} placeholder="#proyectos" data-testid="cms-hero-cta-href" />
            </Field>
            <Field label="CTA secundario · texto">
              <input className={inputCls} value={blocks.hero?.cta_secondary_label || ""} onChange={(e) => update("hero", { cta_secondary_label: e.target.value })} placeholder="Evaluar mi participación" />
            </Field>
          </div>
          <div className="hairline-soft my-1" />
          <div className="text-[10px] uppercase tracking-widest text-[#FFBF57] font-semibold">Video ambiental de fondo (nuevo)</div>
          <Field label="Video ambiental del hero (loop silencioso)" hint="MP4 en loop detrás del texto principal, con overlay para legibilidad. Se optimiza automáticamente al subir (transcode H.264 + faststart web/móvil). Sin límite de duración ni peso. Deja vacío para no mostrar video.">
            <Dropzone value={blocks.hero?.ambient_video_url || ""} onChange={(v) => update("hero", { ambient_video_url: v || "" })} accept="video/*" kind="video" />
          </Field>
          <Field label="Poster del video ambiental (imagen)" hint="Se muestra mientras el video carga">
            <Dropzone value={blocks.hero?.ambient_video_poster || ""} onChange={(v) => update("hero", { ambient_video_poster: v || "" })} accept="image/*" kind="image" />
          </Field>
          <Field label="Opacidad del video (0.0 – 1.0)" hint="0.28 recomendado · valores más altos hacen el video más visible">
            <input type="number" step="0.05" min="0" max="1" className={inputCls} value={blocks.hero?.ambient_video_opacity ?? 0.28} onChange={(e) => update("hero", { ambient_video_opacity: Number(e.target.value) })} />
          </Field>
        </div>
        <SaveBar onSave={() => save("hero")} saving={saving === "hero"} testId="cms-save-hero" />
      </Section>

      {/* Indicators */}
      <Section title="Indicadores · números grandes" subtitle="Sección premium debajo del hero con count-up. Usa {{numero_sucursales}} y {{numero_estados}} para valores automáticos del mapa." defaultOpen>
        <div className="grid gap-4 mb-4">
          <Field label="Eyebrow (opcional)">
            <input className={inputCls} value={blocks.indicators?.eyebrow || ""} onChange={(e) => update("indicators", { eyebrow: e.target.value })} data-testid="cms-indicators-eyebrow" />
          </Field>
          <Field label="Microtexto (pie de sección)">
            <input className={inputCls} value={blocks.indicators?.microcopy || ""} onChange={(e) => update("indicators", { microcopy: e.target.value })} data-testid="cms-indicators-microcopy" />
          </Field>
        </div>
        <ItemsEditor
          items={blocks.indicators?.items || []}
          onChange={(items) => update("indicators", { items })}
          testIdPrefix="cms-indicator"
          template={{ n: "", l: "", sub: "" }}
          fields={[
            { key: "n", label: "Número (ej. 2 · +20 · $5 MDP · {{numero_sucursales}})", type: "text" },
            { key: "l", label: "Etiqueta principal (ej. SUCURSALES)", type: "text" },
            { key: "sub", label: "Texto secundario (ej. de experiencia en perfumería)", type: "text" },
          ]}
        />
        <SaveBar onSave={() => save("indicators")} saving={saving === "indicators"} testId="cms-save-indicators" />
      </Section>

      {/* Expansion note */}
      <Section title="Nota · Nuestra expansión está evolucionando" subtitle="Bloque compacto debajo del mapa (título + párrafo)">
        <div className="grid gap-4">
          <Field label="Título" hint="Usa <em>palabra</em> para acento">
            <input className={inputCls} value={blocks.expansion_note?.title || ""} onChange={(e) => update("expansion_note", { title: e.target.value })} data-testid="cms-expnote-title" />
          </Field>
          <Field label="Párrafo">
            <textarea className={inputCls + " min-h-[90px]"} value={blocks.expansion_note?.body || ""} onChange={(e) => update("expansion_note", { body: e.target.value })} data-testid="cms-expnote-body" />
          </Field>
        </div>
        <SaveBar onSave={() => save("expansion_note")} saving={saving === "expansion_note"} testId="cms-save-expnote" />
      </Section>

      {/* Hero Stats (legacy) */}
      <Section title="KPIs del hero (legado)" subtitle="Ya no se muestran en el hero — usa la sección Indicadores">
        <ItemsEditor
          items={blocks.hero_stats?.items || []}
          onChange={(items) => update("hero_stats", { items })}
          testIdPrefix="cms-herostat"
          template={{ n: "", l: "" }}
          fields={[
            { key: "n", label: "Número (ej. 36 · +50% · 3+2)", type: "text" },
            { key: "l", label: "Etiqueta (ej. sucursales y franquicias)", type: "text" },
          ]}
        />
        <SaveBar onSave={() => save("hero_stats")} saving={saving === "hero_stats"} testId="cms-save-herostats" />
      </Section>

      {/* Round config with welcome video */}
      <Section title="Convocatoria — cierre, cupos y video del fundador · Autoplay nuevo" subtitle="Contador regresivo, métricas y video del fundador en el hero" defaultOpen>
        <div className="grid gap-4">
          <Field label="Video de bienvenida del fundador" hint="Cualquier duración y peso. Se optimiza automáticamente al subir (transcode H.264 + AAC + faststart) para reproducción rápida web y móvil. Se muestra encima del countdown en el hero.">
            <Dropzone value={blocks.round_config?.welcome_video_url || ""} onChange={(v) => update("round_config", { welcome_video_url: v || null })} accept="video/*" kind="video" />
          </Field>
          <Field label="Poster del video (imagen de portada)" hint="Se muestra antes de que el usuario dé play">
            <Dropzone value={blocks.round_config?.welcome_video_poster || ""} onChange={(v) => update("round_config", { welcome_video_poster: v || null })} accept="image/*" kind="image" />
          </Field>
          <Field label="Duración del video (opcional)" hint="Formato mm:ss · e.g. 1:24 — se muestra en la esquina superior del preview">
            <input className={inputCls} value={blocks.round_config?.welcome_video_duration || ""} onChange={(e) => update("round_config", { welcome_video_duration: e.target.value })} placeholder="1:24" data-testid="cms-welcome-video-duration" />
          </Field>
          <Field label="Reproducción automática" hint="Si está activo, el video empieza a reproducirse sin sonido apenas carga la página. Click en el video para abrir en pantalla completa con audio.">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!blocks.round_config?.welcome_video_autoplay}
                onChange={(e) => update("round_config", { welcome_video_autoplay: e.target.checked })}
                className="w-4 h-4 accent-[#FFBF57] cursor-pointer"
                data-testid="cms-welcome-video-autoplay"
              />
              <span className="text-sm text-[#F5F5F0]">
                {blocks.round_config?.welcome_video_autoplay ? "Activado — el video se reproduce en loop automáticamente (sin sonido)" : "Desactivado — muestra el poster con botón play"}
              </span>
            </label>
          </Field>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Fecha y hora de cierre" hint="Ej. 2027-01-01T23:59:59">
              <input type="datetime-local" className={inputCls} value={(blocks.round_config?.closes_at || "").slice(0, 16)} onChange={(e) => update("round_config", { closes_at: e.target.value ? new Date(e.target.value).toISOString() : null })} data-testid="cms-round-closes" />
            </Field>
            <Field label="Cupos totales del círculo">
              <input type="number" className={inputCls} value={blocks.round_config?.spots_total ?? 0} onChange={(e) => update("round_config", { spots_total: Number(e.target.value) })} data-testid="cms-round-total" />
            </Field>
            <Field label="Cupos comprometidos (manual)">
              <input type="number" className={inputCls} value={blocks.round_config?.spots_committed ?? 0} onChange={(e) => update("round_config", { spots_committed: Number(e.target.value) })} data-testid="cms-round-committed" />
            </Field>
          </div>
        </div>
        <SaveBar onSave={() => save("round_config")} saving={saving === "round_config"} testId="cms-save-round" />
      </Section>

      {/* Humanization */}
      <Section title="Humanización · La gente detrás" subtitle="Foto, nombre, rol, cita de fundadores/equipo">
        <div className="grid gap-4">
          <Field label="Eyebrow">
            <input className={inputCls} value={blocks.humanization?.eyebrow || ""} onChange={(e) => update("humanization", { eyebrow: e.target.value })} />
          </Field>
          <Field label="Título" hint="Usa <em>palabra</em> para acento italic">
            <input className={inputCls} value={blocks.humanization?.title || ""} onChange={(e) => update("humanization", { title: e.target.value })} />
          </Field>
          <Field label="Descripción">
            <textarea className={inputCls + " min-h-[90px]"} value={blocks.humanization?.body || ""} onChange={(e) => update("humanization", { body: e.target.value })} />
          </Field>
        </div>
        <div className="mt-4">
          <ItemsEditor
            items={blocks.humanization?.people || []}
            onChange={(people) => update("humanization", { people })}
            testIdPrefix="cms-person"
            template={{ name: "", role: "", quote: "", image_url: "" }}
            fields={[
              { key: "name", label: "Nombre", type: "text" },
              { key: "role", label: "Rol", type: "text" },
              { key: "image_url", label: "Foto", type: "image" },
              { key: "quote", label: "Cita", type: "textarea" },
            ]}
          />
        </div>
        <div className="mt-6">
          <Field label="Título de evidencia">
            <input className={inputCls} value={blocks.humanization?.evidence_title || ""} onChange={(e) => update("humanization", { evidence_title: e.target.value })} />
          </Field>
          <div className="mt-3">
            <ItemsEditor
              items={blocks.humanization?.evidence_items || []}
              onChange={(evidence_items) => update("humanization", { evidence_items })}
              testIdPrefix="cms-evidence"
              template={{ n: "", l: "" }}
              fields={[
                { key: "n", label: "Número", type: "text" },
                { key: "l", label: "Etiqueta", type: "text" },
              ]}
            />
          </div>
        </div>
        <SaveBar onSave={() => save("humanization")} saving={saving === "humanization"} testId="cms-save-humanization" />
      </Section>

      {/* Locations Map */}
      <Section title="Historia · Timeline 2018→hoy→futuro" subtitle="Reemplaza la sección Evolución · milestones interactivos">
        <div className="grid gap-4 mb-4">
          <Field label="Eyebrow"><input className={inputCls} value={blocks.timeline?.eyebrow || ""} onChange={(e) => update("timeline", { eyebrow: e.target.value })} /></Field>
          <Field label="Título" hint="Usa <em>palabra</em> para acento italic"><input className={inputCls} value={blocks.timeline?.title || ""} onChange={(e) => update("timeline", { title: e.target.value })} /></Field>
          <Field label="Subtítulo"><textarea className={inputCls + " min-h-[70px]"} value={blocks.timeline?.subtitle || ""} onChange={(e) => update("timeline", { subtitle: e.target.value })} /></Field>
          <Field label="Microcopy (pie de sección)"><input className={inputCls} value={blocks.timeline?.microcopy || ""} onChange={(e) => update("timeline", { microcopy: e.target.value })} /></Field>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#D4D4D8] mb-2 font-semibold">Capítulos / milestones</div>
        <ItemsEditor
          items={blocks.timeline?.items || []}
          onChange={(items) => update("timeline", { items })}
          testIdPrefix="cms-timeline"
          template={{ year: "", label: "", icon: "trending", title: "", body: "", image_url: "", metrics: [] }}
          fields={[
            { key: "year", label: "Año (ej. 2018, 2027+)", type: "text" },
            { key: "label", label: "Etiqueta corta (ej. Fundación, Escala nacional)", type: "text" },
            { key: "icon", label: "Icono", type: "select", options: [
              { v: "store", l: "Tienda" },
              { v: "building", l: "Edificio" },
              { v: "trophy", l: "Trofeo" },
              { v: "trending", l: "Tendencia" },
              { v: "sparkles", l: "Destello" },
              { v: "rocket", l: "Cohete" },
              { v: "map", l: "Pin ubicación" },
            ]},
            { key: "title", label: "Título del capítulo", type: "text" },
            { key: "body", label: "Descripción", type: "textarea" },
            { key: "image_url", label: "Imagen ilustrativa", type: "image" },
          ]}
        />
        <SaveBar onSave={() => save("timeline")} saving={saving === "timeline"} testId="cms-save-timeline" />
      </Section>

      {/* Locations Map */}
      <Section title="Mapa de sucursales" subtitle="Todas se muestran públicamente como 'Sucursal Rubí Perfumería' con carrusel sincronizado. El campo Estado/categoría se conserva para uso interno.">
        <div className="grid gap-4 mb-4">
          <Field label="Eyebrow">
            <input className={inputCls} value={blocks.locations_map?.eyebrow || ""} onChange={(e) => update("locations_map", { eyebrow: e.target.value })} />
          </Field>
          <Field label="Título" hint="Usa <em>palabra</em> para acento italic">
            <input className={inputCls} value={blocks.locations_map?.title || ""} onChange={(e) => update("locations_map", { title: e.target.value })} />
          </Field>
          <Field label="Subtítulo">
            <textarea className={inputCls + " min-h-[80px]"} value={blocks.locations_map?.subtitle || ""} onChange={(e) => update("locations_map", { subtitle: e.target.value })} />
          </Field>
          <Field label="Microcopy del mapa">
            <input className={inputCls} value={blocks.locations_map?.microcopy || ""} onChange={(e) => update("locations_map", { microcopy: e.target.value })} />
          </Field>
        </div>
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-widest text-[#D4D4D8] mb-2 font-semibold">Estadísticas destacadas</div>
          <ItemsEditor
            items={blocks.locations_map?.stats || []}
            onChange={(stats) => update("locations_map", { stats })}
            testIdPrefix="cms-locstat"
            template={{ n: "", l: "" }}
            fields={[
              { key: "n", label: "Número", type: "text" },
              { key: "l", label: "Etiqueta", type: "text" },
            ]}
          />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#D4D4D8] mb-2 font-semibold">Pines en el mapa</div>
          <ItemsEditor
            items={blocks.locations_map?.items || []}
            onChange={(items) => update("locations_map", { items })}
            testIdPrefix="cms-location"
            template={{ name: "", city: "", state: "", status: "activa", lat: 19.4326, lng: -99.1332, address: "", phone: "", hours: "", image_url: "", opened_at: "", maps_url: "", note: "" }}
            fields={[
              { key: "name", label: "Nombre de la sucursal", type: "text" },
              { key: "city", label: "Ciudad", type: "text" },
              { key: "state", label: "Estado de la república (ej. Oaxaca)", type: "text", hint: "Se usa para contar {{numero_estados}} en Indicadores" },
              { key: "image_url", label: "Fotografía de la sucursal", type: "image" },
              { key: "address", label: "Dirección", type: "textarea" },
              { key: "phone", label: "Teléfono", type: "text" },
              { key: "hours", label: "Horario", type: "text" },
              { key: "badge", label: "Etiqueta sobre la foto (ej. Próxima inauguración · 13 de septiembre)", type: "text" },
              { key: "status", label: "Categoría interna (no visible al público)", type: "select", options: [
                { v: "activa", l: "En operación" },
                { v: "fundadora", l: "Sucursal fundadora" },
                { v: "franquicia", l: "Franquicia" },
                { v: "proxima", l: "Próxima apertura" },
              ] },
              { key: "lat", label: "Latitud (ej. 19.4326)", type: "text", hint: "Google Maps: click derecho en la ubicación → copia lat" },
              { key: "lng", label: "Longitud (ej. -99.1332)", type: "text" },
              { key: "maps_url", label: "Link de Google Maps (opcional)", type: "text", hint: "Pega aquí la URL de Google Maps. Aparece un botón 'Ver en Google Maps' en el popup." },
              { key: "opened_at", label: "Año de apertura / plazo", type: "text" },
              { key: "note", label: "Nota / detalle (aparece en el popup)", type: "text" },
            ]}
          />
        </div>
        <SaveBar onSave={() => save("locations_map")} saving={saving === "locations_map"} testId="cms-save-locations" />
      </Section>

      {/* Projects */}
      <Section title="Proyectos disponibles" subtitle="Imagen del proyecto + sucursales anidadas (cada una con su foto y estado)">
        <div className="grid gap-4 mb-4">
          <Field label="Título de la sección">
            <input className={inputCls} value={blocks.projects?.title || ""} onChange={(e) => update("projects", { title: e.target.value })} />
          </Field>
          <Field label="Nota (párrafo lateral)">
            <textarea className={inputCls + " min-h-[70px]"} value={blocks.projects?.note || ""} onChange={(e) => update("projects", { note: e.target.value })} />
          </Field>
        </div>
        <ItemsEditor
          items={blocks.projects?.items || []}
          onChange={(items) => update("projects", { items })}
          testIdPrefix="cms-project"
          template={{ code: "", city: "", type: "", stage: "Disponible", capital_target: 0, capital_committed: 0, spots_left: 0, updated_at: "", note: "", sucursales_target: 5, sucursales: [] }}
          fields={[
            { key: "code", label: "Nombre del proyecto", type: "text" },
            { key: "city", label: "Ciudad / plaza", type: "text" },
            { key: "type", label: "Tipo (Plaza premium, Corredor, etc.)", type: "text" },
            { key: "stage", label: "Etapa", type: "select", options: [
              { v: "Disponible", l: "Disponible" },
              { v: "Evaluación privada", l: "Evaluación privada" },
              { v: "En revisión privada", l: "En revisión privada" },
              { v: "En documentación", l: "En documentación" },
              { v: "Próximo a completar", l: "Próximo a completar" },
              { v: "Capital completado", l: "Capital completado" },
            ] },
            { key: "image_url", label: "Imagen principal del proyecto", type: "image" },
            { key: "sqm", label: "Superficie (ej. 120 m²)", type: "text" },
            { key: "opens", label: "Apertura (ej. Q4 2026)", type: "text" },
            { key: "traffic", label: "Flujo / público (ej. Alto poder adquisitivo)", type: "text" },
            { key: "capital_target", label: "Capital objetivo (MXN, sólo número)", type: "text" },
            { key: "capital_committed", label: "Capital formalizado (MXN, sólo número)", type: "text" },
            { key: "spots_left", label: "Participaciones restantes", type: "text" },
            { key: "updated_at", label: "Actualizado (YYYY-MM-DD)", type: "text" },
            { key: "sucursales_target", label: "Sucursales totales del proyecto", type: "text" },
            { key: "sucursales", label: "Sucursales anidadas (foto + estado individual)", type: "nested-sucursales" },
            { key: "note", label: "Nota / observación", type: "text" },
          ]}
        />
        <SaveBar onSave={() => save("projects")} saving={saving === "projects"} testId="cms-save-projects" />
      </Section>

      {/* Casos de éxito con videos verticales */}
      <Section title="Casos de éxito (videos verticales 9:16)" subtitle="Testimonios en video de sucursales existentes">
        <div className="grid gap-4">
          <Field label="Título de la sección">
            <input className={inputCls} value={blocks.case_studies?.title || ""} onChange={(e) => update("case_studies", { title: e.target.value })} />
          </Field>
          <Field label="Subtítulo">
            <input className={inputCls} value={blocks.case_studies?.subtitle || ""} onChange={(e) => update("case_studies", { subtitle: e.target.value })} />
          </Field>
        </div>
        <div className="mt-4">
          <ItemsEditor
            items={blocks.case_studies?.items || []}
            onChange={(items) => update("case_studies", { items })}
            testIdPrefix="cms-case"
            template={{ title: "", city: "", metric: "", body: "", image_url: "", video_url: "", tag: "" }}
            fields={[
              { key: "title", label: "Título del caso", type: "text" },
              { key: "city", label: "Ciudad / región", type: "text" },
              { key: "metric", label: "Métrica destacada (chip amarillo)", type: "text" },
              { key: "body", label: "Descripción", type: "textarea" },
              { key: "tag", label: "Etiqueta (Caso cerrado / Track record / En curso)", type: "text" },
              { key: "image_url", label: "Imagen vertical (poster)", type: "image" },
              { key: "video_url", label: "Video vertical MP4 (9:16)", type: "video" },
            ]}
          />
        </div>
        <SaveBar onSave={() => save("case_studies")} saving={saving === "case_studies"} testId="cms-save-cases" />
      </Section>

      {/* Gallery */}
      <Section title="Galería visual" subtitle="Fotos de producto, sucursales, ambiente">
        <div className="grid gap-4 mb-4">
          <Field label="Título">
            <input className={inputCls} value={blocks.gallery?.title || ""} onChange={(e) => update("gallery", { title: e.target.value })} />
          </Field>
          <Field label="Subtítulo">
            <textarea className={inputCls + " min-h-[70px]"} value={blocks.gallery?.subtitle || ""} onChange={(e) => update("gallery", { subtitle: e.target.value })} />
          </Field>
        </div>
        <ItemsEditor
          items={blocks.gallery?.items || []}
          onChange={(items) => update("gallery", { items })}
          testIdPrefix="cms-gallery"
          template={{ image_url: "" }}
          fields={[
            { key: "image_url", label: "Imagen", type: "image" },
          ]}
        />
        <SaveBar onSave={() => save("gallery")} saving={saving === "gallery"} testId="cms-save-gallery" />
      </Section>

      {/* Mobile App */}
      <Section title="App móvil — Próximamente" subtitle="Título, features, fecha">
        <div className="grid gap-4">
          <Field label="Eyebrow"><input className={inputCls} value={blocks.mobile_app?.eyebrow || ""} onChange={(e) => update("mobile_app", { eyebrow: e.target.value })} /></Field>
          <Field label="Título"><input className={inputCls} value={blocks.mobile_app?.title || ""} onChange={(e) => update("mobile_app", { title: e.target.value })} /></Field>
          <Field label="Descripción"><textarea className={inputCls + " min-h-[90px]"} value={blocks.mobile_app?.body || ""} onChange={(e) => update("mobile_app", { body: e.target.value })} /></Field>
          <Field label="Fecha estimada"><input className={inputCls} value={blocks.mobile_app?.available_at || ""} onChange={(e) => update("mobile_app", { available_at: e.target.value })} placeholder="Q3 2026" /></Field>
          <Field label="Features (una por línea)">
            <textarea className={inputCls + " min-h-[100px]"} value={(blocks.mobile_app?.features || []).join("\n")} onChange={(e) => update("mobile_app", { features: e.target.value.split("\n").filter(Boolean) })} />
          </Field>
        </div>
        <SaveBar onSave={() => save("mobile_app")} saving={saving === "mobile_app"} testId="cms-save-app" />
      </Section>

      {/* CTA final */}
      <Section title="CTA final del landing" subtitle="Sección oscura al final antes del footer">
        <div className="grid gap-4">
          <Field label="Eyebrow"><input className={inputCls} value={blocks.cta_final?.eyebrow || ""} onChange={(e) => update("cta_final", { eyebrow: e.target.value })} data-testid="cms-cta-eyebrow" /></Field>
          <Field label="Título"><textarea className={inputCls + " min-h-[80px]"} value={blocks.cta_final?.title || ""} onChange={(e) => update("cta_final", { title: e.target.value })} data-testid="cms-cta-title" /></Field>
          <Field label="Cuerpo"><textarea className={inputCls + " min-h-[90px]"} value={blocks.cta_final?.body || ""} onChange={(e) => update("cta_final", { body: e.target.value })} data-testid="cms-cta-body" /></Field>
          <Field label="Microcopy inferior"><input className={inputCls} value={blocks.cta_final?.microcopy || ""} onChange={(e) => update("cta_final", { microcopy: e.target.value })} /></Field>
        </div>
        <SaveBar onSave={() => save("cta_final")} saving={saving === "cta_final"} testId="cms-save-cta" />
      </Section>

      {/* FAQ */}
      <Section title="Preguntas frecuentes (FAQ)">
        <ItemsEditor
          items={blocks.faq?.items || []}
          onChange={(items) => update("faq", { items })}
          testIdPrefix="cms-faq"
          template={{ q: "", a: "" }}
          fields={[
            { key: "q", label: "Pregunta", type: "text" },
            { key: "a", label: "Respuesta", type: "textarea" },
          ]}
        />
        <SaveBar onSave={() => save("faq")} saving={saving === "faq"} testId="cms-save-faq" />
      </Section>

      {/* Founders */}
      <Section title="Fundadores">
        <div className="grid gap-4">
          <Field label="Título"><input className={inputCls} value={blocks.founders?.title || ""} onChange={(e) => update("founders", { title: e.target.value })} /></Field>
          <Field label="Descripción"><textarea className={inputCls + " min-h-[90px]"} value={blocks.founders?.body || ""} onChange={(e) => update("founders", { body: e.target.value })} /></Field>
          <Field label="Cita"><textarea className={inputCls + " min-h-[70px]"} value={blocks.founders?.quote || ""} onChange={(e) => update("founders", { quote: e.target.value })} /></Field>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Autor de cita"><input className={inputCls} value={blocks.founders?.quote_author || ""} onChange={(e) => update("founders", { quote_author: e.target.value })} /></Field>
            <Field label="Rol del autor"><input className={inputCls} value={blocks.founders?.quote_role || ""} onChange={(e) => update("founders", { quote_role: e.target.value })} /></Field>
          </div>
        </div>
        <SaveBar onSave={() => save("founders")} saving={saving === "founders"} testId="cms-save-founders" />
      </Section>
    </div>
  );
}
